/**
 * @fileoverview Restaurant Row Actions Component
 * Dropdown menu with actions for each restaurant row in the data table
 */

// External imports
import React, { useState } from "react";
import {
  Heart,
  MoreHorizontal,
  ExternalLink,
  Trash2,
  FolderPlus,
  MessageSquare,
  Plus,
} from "lucide-react";

// UI Components
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Internal imports
import { Restaurant } from "../types/restaurants.types";
import { useRemoveRestaurant } from "../hooks/use-save-restaurant";
import { useRemoveFromList } from "../../lists/hooks/use-lists";
import { AddToListModal } from "../../lists/components/add-to-list-modal";
import { useRestaurantReviewManager } from "../hooks/use-reviews";
import { ReviewModal } from "./review-modal";
import { useRestaurantHearts, useToggleHeart } from "../hooks/use-hearts";
import { CreateReviewRequest, UpdateReviewRequest } from "@/types/reviews.types";

interface RestaurantRowActionsProps {
  restaurant: Restaurant;
  listId?: string;  // When provided, shows "Remove from List" instead of "Remove from Favorites"
  /** Hide the remove action, for places the user may not have saved (public lists, homepage). */
  hideRemove?: boolean;
}

/**
 * Row actions component for restaurant data table
 */
export const RestaurantRowActions: React.FC<RestaurantRowActionsProps> = ({
  restaurant,
  listId,
  hideRemove = false,
}) => {
  const [showReviewModal, setShowReviewModal] = useState(false);

  const removeRestaurant = useRemoveRestaurant();
  const removeFromList = useRemoveFromList();
  const reviewManager = useRestaurantReviewManager(restaurant.id);
  // Hearts are fetched only once the menu opens, so rendering a table costs no requests per row.
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: heartsData } = useRestaurantHearts(restaurant.id, menuOpen);
  const toggleHeart = useToggleHeart(restaurant.id);
  const hearted = heartsData?.heartedByMe ?? false;

  const isRemoving = listId ? removeFromList.isPending : removeRestaurant.isPending;

  const handleRemove = async () => {
    try {
      if (listId) {
        await removeFromList.mutateAsync({ listId, restaurantId: restaurant.id });
      } else {
        await removeRestaurant.mutateAsync(restaurant.id);
      }
    } catch (error) {
      console.error("Failed to remove restaurant:", error);
    }
  };

  const handleViewOnMap = () => {
    const query = encodeURIComponent(
      `${restaurant.name} ${restaurant.formattedAddress}`
    );
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${query}`,
      "_blank"
    );
  };

  const handleReviewSubmit = async (
    data: CreateReviewRequest | UpdateReviewRequest
  ) => {
    if (reviewManager.hasUserReview) {
      await reviewManager.updateReview(data as UpdateReviewRequest);
    } else {
      await reviewManager.createReview({
        ...data,
        restaurantId: restaurant.id,
      } as CreateReviewRequest);
    }
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            aria-label="Open actions menu"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {/* Review Actions */}
          {reviewManager.hasUserReview ? (
            <DropdownMenuItem onClick={() => setShowReviewModal(true)}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Edit Review
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => setShowReviewModal(true)}
              disabled={reviewManager.isCreating}
            >
              {reviewManager.isCreating ? (
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Add Review
            </DropdownMenuItem>
          )}

          <AddToListModal
            restaurant={restaurant}
            trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <FolderPlus className="mr-2 h-4 w-4" />
                Add to List
              </DropdownMenuItem>
            }
          />
          <DropdownMenuItem onClick={() => toggleHeart.mutate(!hearted)} disabled={!heartsData}>
            <Heart className="mr-2 h-4 w-4 fill-brand text-brand" />
            {hearted ? "Unheart" : "Heart"}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleViewOnMap}>
            <ExternalLink className="mr-2 h-4 w-4" />
            View on Google Maps
          </DropdownMenuItem>
          {!hideRemove && (
            <>
    
              <DropdownMenuSeparator />
    
              <DropdownMenuItem
                onClick={handleRemove}
                disabled={isRemoving}
                className="text-brand focus:text-brand"
              >
                {isRemoving ? (
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                {listId ? "Remove from List" : "Remove from Favorites"}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        restaurantName={restaurant.name}
        existingReview={reviewManager.userReview}
        onSubmit={handleReviewSubmit}
        isLoading={reviewManager.isMutating}
      />

    </>
  );
};
